import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Hint } from "@/components/devicePanel/Hint";
import type { VerdictInput } from "@/hooks/useDevicePanel";
import { verdictErrorText } from "@/hooks/useDevicePanel";
import {
  NOTE_MAX_LENGTH,
  REVIEW_TAGS,
  ROUND_PROMPTS,
  VERDICTS,
  findingParam,
  othersHint,
  parseFindingParam,
  roundOf,
  sameTarget,
  sameVerdictSlot,
  verdictLabel,
  type RailRound,
  type ReviewItem,
  type ReviewRound,
  type ReviewTarget,
  type StudyReviewResponse,
  type VerdictValue,
} from "@/types/devicePanelReview";

/** The caller's review of a study: queue items and verdicts of every round, and a save. */
export interface StudyReviewModel {
  data: StudyReviewResponse;
  /** Resolves once saved; rejects with the backend's reason. */
  save: (input: VerdictInput) => Promise<unknown>;
}

/** The study's review as the rail reads it: the round being reviewed, and a step
 * through the round's queue. */
export interface ReviewModel extends StudyReviewModel {
  round: RailRound;
  /** Next unjudged item (1) or previous item (-1) in the round's queue, from the
   * current row, across studies when this one has none that way. */
  step: (direction: 1 | -1) => void;
}

interface ReviewContextValue {
  review: ReviewModel | null;
  /** The finding a deep link names; null without one. */
  link: ReviewTarget | null;
  /** Whether the viewer has laid out its panes, so a jump is not overwritten by
   * its opening layout. */
  viewerReady: boolean;
  /** True the first time it is called for the current link, so its row jumps once. */
  takeLink: () => boolean;
  /** The review row last focused (`findingParam` of its item), which Next and
   * Previous step from. */
  current: string | null;
  setCurrent: (key: string) => void;
}

const ReviewContext = createContext<ReviewContextValue>({
  review: null,
  link: null,
  viewerReady: false,
  takeLink: () => false,
  current: null,
  setCurrent: () => {},
});

export function ReviewProvider({
  review,
  link,
  viewerReady,
  children,
}: {
  review: ReviewModel | null;
  link: ReviewTarget | null;
  viewerReady: boolean;
  children: ReactNode;
}) {
  const taken = useRef<string | null>(null);
  const [current, setCurrent] = useState<string | null>(null);
  const linkKey = link && findingParam(link);
  const takeLink = () => {
    if (!linkKey || taken.current === linkKey) return false;
    taken.current = linkKey;
    return true;
  };
  return (
    <ReviewContext.Provider value={{ review, link, viewerReady, takeLink, current, setCurrent }}>
      {children}
    </ReviewContext.Provider>
  );
}

/** The queue items of the round being reviewed. */
const roundItems = (review: ReviewModel | null): ReviewItem[] =>
  (review?.data.items ?? []).filter((item) => roundOf(item.kind) === review!.round);

/** Whether a target is in the round's review queue (false while the review loads). */
function useIsQueued(): (target: ReviewTarget) => boolean {
  const { review } = useContext(ReviewContext);
  const items = roundItems(review);
  return (target) => items.some((item) => sameTarget(item, target));
}

/** Whether a target's row must show however low its call: queued in the round,
 * or named by the deep link. */
export function useIsShown(): (target: ReviewTarget) => boolean {
  const { link } = useContext(ReviewContext);
  const isQueued = useIsQueued();
  return (target) => isQueued(target) || (!!link && sameTarget(link, target));
}

/** The tags of a row's targets queued in the round, e.g. "Not in report". */
export function ReviewTags({ targets }: { targets: ReviewTarget[] }) {
  const { review } = useContext(ReviewContext);
  const tags = [
    ...new Set(
      roundItems(review)
        .filter((item) => targets.some((target) => sameTarget(item, target)))
        .map((item) => REVIEW_TAGS[item.kind]),
    ),
  ];
  return tags.map((tag) => (
    <span
      key={tag}
      data-review-tag
      className="ml-1.5 whitespace-nowrap rounded-xs border border-border-strong px-1 align-middle font-mono text-[9.5px] text-fg-muted"
    >
      {tag}
    </span>
  ));
}

/** Whether a target's blind verdict is locked: once a finding is queued for the
 * report check, the backend refuses blind writes on it. */
const isLocked = (review: ReviewModel, target: ReviewTarget) =>
  review.round === "blind" &&
  review.data.items.some((item) => item.kind === "report_check" && sameTarget(item, target));

const LOCKED_REASON =
  "Locked: this finding is in the report check, so its blind verdict no longer changes.";

/** A locked blind verdict: the chosen verdict and note without buttons, and a
 * "locked" cue with the reason on hover or focus. */
function LockedVerdict({ target, label }: { target: ReviewTarget; label: string | null }) {
  const { data } = useContext(ReviewContext).review!;
  const current = data.verdicts.find((verdict) =>
    sameVerdictSlot(verdict, { ...target, round: "blind" }),
  );
  const others = othersHint(data.other_verdicts, target, "blind");
  return (
    <div
      data-verdict-control
      data-locked
      className="group/verdict flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] text-fg-dim"
    >
      {label && <span className="font-mono text-fg-muted">{label}</span>}
      <span>On images:</span>
      <Hint label={others}>
        <span
          data-locked-verdict
          tabIndex={others ? 0 : undefined}
          className={
            current
              ? "rounded-xs border border-signal bg-signal px-1.5 py-px text-fg"
              : "text-fg-muted"
          }
        >
          {current ? verdictLabel("blind", current.verdict) : "no verdict"}
        </span>
      </Hint>
      <Hint label={LOCKED_REASON}>
        <span
          tabIndex={0}
          aria-label={LOCKED_REASON}
          className="rounded-xs underline decoration-dotted underline-offset-2 focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal"
        >
          locked
        </span>
      </Hint>
      {current?.note && (
        <span className={`${NOTE_LINE} truncate border border-transparent text-fg-muted`}>
          {current.note}
        </span>
      )}
    </div>
  );
}

// The note field and its folded preview share one line box, so folding or
// unfolding a note never changes the row's height.
const NOTE_LINE = "block h-5 basis-full text-[11px] leading-[18px]";

type SaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "error"; text: string };

const SAVED_FADE_MS = 1500;

/**
 * One device's call, judged in a round, with an optional note (enabled once a
 * verdict exists). Blind read: "On images: Present · Absent · Unsure". Report
 * check: the reader's own blind verdict ("You: Present"), the report's sentence
 * about the finding, then "Keep · Revise · Unsure". Key image: "Key image: Shows
 * it · Wrong slice · Unsure". Missed: as the blind read. The chosen
 * verdict takes a solid signal fill; the label always says which. Saving updates
 * at once; "Saved" fades, and a failure says why on hover, in a fixed slot so the
 * row never moves. Other reviewers' verdicts in the round show in a tooltip on
 * the buttons.
 */
export function VerdictControl({
  review,
  round,
  target,
  label,
}: {
  review: StudyReviewModel;
  round: ReviewRound;
  target: ReviewTarget;
  label: string | null;
}) {
  const { data } = review;
  const current = data.verdicts.find((verdict) => sameVerdictSlot(verdict, { ...target, round }));
  const others = othersHint(data.other_verdicts, target, round);
  const [state, setState] = useState<SaveState>({ kind: "idle" });
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(current?.note ?? "");

  useEffect(() => {
    if (state.kind !== "saved") return;
    const timer = window.setTimeout(() => setState({ kind: "idle" }), SAVED_FADE_MS);
    return () => window.clearTimeout(timer);
  }, [state]);

  const save = async (verdict: VerdictValue, noteText: string | null) => {
    setState({ kind: "saving" });
    try {
      await review.save({ ...target, round, verdict, note: noteText });
      setState({ kind: "saved" });
    } catch (error) {
      setState({ kind: "error", text: verdictErrorText(error) });
    }
  };
  const savedNote = current?.note ?? "";
  const commitNote = () => {
    if (current && note !== savedNote) void save(current.verdict, note || null);
  };
  const groupName = ROUND_PROMPTS[round];

  return (
    <div
      data-verdict-control
      className="group/verdict flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] text-fg-dim"
    >
      {label && <span className="font-mono text-fg-muted">{label}</span>}
      {round === "report_check" ? <ReportContext target={target} /> : <span>{groupName}:</span>}
      <Hint label={others}>
        <span role="group" aria-label={`${groupName}${label ? `, ${label}` : ""}`} className="flex">
          {VERDICTS[round].map(({ value, label: text }) => (
            <button
              key={value}
              type="button"
              data-verdict={value}
              aria-pressed={current?.verdict === value}
              onClick={() => void save(value, current?.note ?? null)}
              className="-ml-px border border-border-strong px-1.5 py-px text-fg-muted first:ml-0 first:rounded-l-xs last:rounded-r-xs hover:text-fg focus-visible:relative focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal aria-pressed:relative aria-pressed:border-signal aria-pressed:bg-signal aria-pressed:text-fg"
            >
              {text}
            </button>
          ))}
        </span>
      </Hint>
      <button
        type="button"
        data-note-toggle
        aria-expanded={noteOpen}
        disabled={!current}
        onClick={() => setNoteOpen(!noteOpen)}
        className="underline-offset-2 hover:text-fg hover:underline disabled:cursor-default disabled:opacity-50 disabled:hover:no-underline"
      >
        Note
      </button>
      {/* A fixed slot, so the save state never moves the row. */}
      <span role="status" data-save-state className="w-14 shrink-0 whitespace-nowrap">
        {state.kind === "saving" && "Saving…"}
        {state.kind === "saved" && "Saved"}
        {state.kind === "error" && (
          <Hint label={state.text}>
            <span tabIndex={0} className="text-warn underline decoration-dotted underline-offset-2">
              Not saved<span className="sr-only">: {state.text}</span>
            </span>
          </Hint>
        )}
      </span>
      {noteOpen && current && (
        <input
          aria-label="Note"
          autoFocus
          value={note}
          maxLength={NOTE_MAX_LENGTH}
          onChange={(event) => setNote(event.target.value)}
          onBlur={commitNote}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Enter") {
              commitNote();
              setNoteOpen(false);
            }
            if (event.key === "Escape") {
              setNote(savedNote);
              setNoteOpen(false);
            }
          }}
          className={`${NOTE_LINE} rounded-xs border border-border-strong bg-bg px-1.5 text-fg focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal`}
        />
      )}
      {!noteOpen && savedNote && (
        <button
          type="button"
          data-note-preview
          aria-label={`Edit note: ${savedNote}`}
          onClick={() => setNoteOpen(true)}
          className={`${NOTE_LINE} truncate border border-transparent text-left text-fg-muted hover:text-fg`}
        >
          {savedNote}
        </button>
      )}
    </div>
  );
}

/** What a report-check verdict is given against: the reader's own blind verdict
 * on the finding, then the report's sentence about it in a quiet quoted block. */
function ReportContext({ target }: { target: ReviewTarget }) {
  const { data } = useContext(ReviewContext).review!;
  const item = data.items.find(
    (entry) => entry.kind === "report_check" && sameTarget(entry, target),
  );
  if (item?.kind !== "report_check")
    throw new Error(`${findingParam(target)} is not queued for the report check`);
  const blind = data.verdicts.find((verdict) =>
    sameVerdictSlot(verdict, { ...target, round: "blind" }),
  );
  return (
    <>
      <span data-blind-verdict className="basis-full">
        You: {blind ? verdictLabel("blind", blind.verdict) : "no blind verdict"}
      </span>
      <blockquote
        data-report-phrase
        className="basis-full border-l-2 border-border-strong pl-2 text-[11.5px] text-fg-muted"
      >
        Report: “{item.report_phrase}”
      </blockquote>
    </>
  );
}

const railRows = () =>
  Array.from(document.querySelectorAll<HTMLElement>("[data-device-panel-rail] [data-review-row]"));

/** Focus a review row and jump the viewer to it. */
function goToRow(row: HTMLElement) {
  const jump = row.querySelector<HTMLButtonElement>("[data-jump]")!;
  jump.focus();
  jump.click();
}

/**
 * Step through this study's review rows in rail order from the current one:
 * forward to the next unjudged row, back to the previous row. With `wrap`, a
 * forward step that finds none after the current row takes the first unjudged
 * one before it. False when there is none, so the step moves to another study.
 */
export function stepInStudy(direction: 1 | -1, wrap = false): boolean {
  const rows = railRows();
  const at = rows.findIndex((row) => row.hasAttribute("data-current"));
  const unjudged = (row: HTMLElement) => !row.hasAttribute("data-judged");
  const target =
    direction === 1
      ? (rows.slice(at + 1).find(unjudged) ?? (wrap ? rows.slice(0, at).find(unjudged) : undefined))
      : at > 0
        ? rows[at - 1]
        : undefined;
  if (!target) return false;
  goToRow(target);
  return true;
}

/** The item a study opens on at `at`: its first unjudged row (else its first),
 * or its last row; null while the rail has no review rows. */
export function rowAt(at: "next" | "last"): ReviewTarget | null {
  const rows = railRows();
  const row =
    at === "next"
      ? (rows.find((candidate) => !candidate.hasAttribute("data-judged")) ?? rows[0])
      : rows.at(-1);
  return row ? parseFindingParam(row.dataset.reviewItem!) : null;
}

/**
 * A finding row, with a verdict line per device call in Review (labelled with
 * the device only when the row holds several): in the blind read on every row,
 * in the report check on the queued ones (the backend takes report-check
 * verdicts only for them). The line is always there, never on hover, so nothing
 * moves while reviewing. A queued row carries a signal edge and its kind tag. A
 * click anywhere on the row focuses it (Safari, for one, does not focus a
 * clicked button). While a row with a verdict line has focus, 1 / 2 / 3 set the
 * round's verdicts (Present / Absent / Unsure, or Keep / Revise / Unsure) for its
 * first unjudged queued call (else its first editable one), n opens that call's
 * note, and j / k step to the next unjudged / previous item of the round's
 * queue, across studies; the panel takes those keys, so the viewer's own
 * bindings for them (bare 1-5 are window presets) do not fire. With no such row
 * focused they keep their viewer meaning. The row a deep link
 * names scrolls into view, takes focus and jumps to its key slice once the
 * viewer has laid out its panes and the row is `ready`.
 */
export function ReviewRow({
  targets,
  labels,
  indent,
  ready,
  children,
}: {
  targets: ReviewTarget[];
  /** Device short names, in `targets` order. */
  labels: string[];
  /** Left padding that lines the controls up under the finding name. */
  indent: string;
  /** Whether the row's jump can land (its key slice is known to the viewer, or it
   * has none). */
  ready: boolean;
  children: ReactNode;
}) {
  const { review, link, viewerReady, takeLink, current, setCurrent } = useContext(ReviewContext);
  const isQueued = useIsQueued();
  const rowRef = useRef<HTMLDivElement>(null);
  const linked = !!link && targets.some((target) => sameTarget(target, link));
  useEffect(() => {
    if (!linked || !ready || !viewerReady || !takeLink()) return;
    const row = rowRef.current!;
    row.scrollIntoView({ block: "center" });
    const jump = row.querySelector<HTMLButtonElement>("[data-jump]")!;
    jump.focus({ preventScroll: true });
    jump.click();
  }, [linked, ready, viewerReady, takeLink]);

  const controls = review
    ? targets
        .map((target, index) => ({
          target,
          label: labels[index],
          queued: isQueued(target),
          judged: review.data.verdicts.some((verdict) =>
            sameVerdictSlot(verdict, { ...target, round: review.round }),
          ),
          locked: isLocked(review, target),
        }))
        .filter(({ queued }) => review.round === "blind" || queued)
    : [];
  const queuedControls = controls.filter((control) => control.queued);
  const queued = queuedControls.length > 0;
  // A locked call cannot be judged here, so stepping passes over it.
  const judged = queued && queuedControls.every((control) => control.judged || control.locked);
  const queuedPrimary =
    queuedControls.find((control) => !control.judged && !control.locked) ?? queuedControls[0];
  const keyControl = queuedPrimary ?? controls.find((control) => !control.locked) ?? controls[0];
  const item = queuedPrimary && findingParam(queuedPrimary.target);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!review || !keyControl || event.target instanceof HTMLInputElement) return;
    const row = event.currentTarget;
    const control =
      row.querySelectorAll<HTMLElement>("[data-verdict-control]")[controls.indexOf(keyControl)];
    const editable = !keyControl.locked;
    const verdict = editable && VERDICTS[review.round].find(({ key }) => key === event.key)?.value;
    const act = verdict
      ? () => control.querySelector<HTMLButtonElement>(`[data-verdict="${verdict}"]`)!.click()
      : editable && event.key === "n"
        ? () => control.querySelector<HTMLButtonElement>("[data-note-toggle]")!.click()
        : event.key === "j" || event.key === "k"
          ? () => review.step(event.key === "j" ? 1 : -1)
          : null;
    if (!act) return;
    event.preventDefault();
    event.stopPropagation();
    act();
  };

  return (
    <div
      ref={rowRef}
      data-review-row={queued || undefined}
      data-review-item={item}
      data-judged={judged || undefined}
      data-current={(!!item && item === current) || undefined}
      data-linked={linked || undefined}
      onFocus={() => item && setCurrent(item)}
      onClick={(event) => {
        const row = event.currentTarget;
        if (!row.contains(document.activeElement))
          row.querySelector<HTMLElement>("[data-jump]")?.focus({ preventScroll: true });
      }}
      onKeyDown={onKeyDown}
      className={`group/row ${queued ? "shadow-[inset_2px_0_0_var(--color-signal)]" : ""}`}
    >
      {children}
      {controls.length > 0 && (
        <div data-review-controls className={`flex flex-col gap-1 pb-1.5 pr-3 ${indent}`}>
          {controls.map(({ target, label, locked }) =>
            locked ? (
              <LockedVerdict
                key={`${target.device}|${target.condition}`}
                target={target}
                label={targets.length > 1 ? label : null}
              />
            ) : (
              <VerdictControl
                key={`${target.device}|${target.condition}`}
                review={review!}
                round={review!.round}
                target={target}
                label={targets.length > 1 ? label : null}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}
